import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import { Home, About, Contact, Privacy, Impressum, UeberSeidelsImmobilien } from './utils/lazyImports';

// Lazy load newsletter pages
const NewsletterPage = lazy(() => import('./pages/Newsletter'));
const NewsletterSubscription = lazy(() => import('./pages/NewsletterSubscription'));
const NewsletterConfirmation = lazy(() => import('./pages/NewsletterConfirmation'));

// Lazy load blog pages
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Lazy load lead form page
const LeadFormular = lazy(() => import('./pages/LeadFormular'));

// Lazy load new pages
const FAQ = lazy(() => import('./pages/FAQ'));
const Einzugsgebiete = lazy(() => import('./pages/Einzugsgebiete'));
const ChecklisteDownload = lazy(() => import('./pages/ChecklisteDownload'));
const Kapitalanlage = lazy(() => import('./pages/Kapitalanlage'));
const KapitalanlageKaufen = lazy(() => import('./pages/KapitalanlageKaufen'));
const KapitalanlagenGuide = lazy(() => import('./pages/KapitalanlagenGuide'));

// Extend window interface for Google Analytics function
declare global {
  interface Window {
    loadGoogleAnalytics?: () => void;
  }
}

// Lazy load non-critical components
const CookieConsent = lazy(() => import('./components/ui/CookieConsent'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view with Google Tag Manager for SPA navigation
    // Small delay to ensure title is updated
    setTimeout(() => {
      if (typeof (window as any).gtag === 'function') {
        // Send page view to Google Ads
        (window as any).gtag('config', 'AW-17412433587', {
          page_path: pathname,
          page_location: window.location.href,
          page_title: document.title
        });
        
        // Send page view to GA4
        (window as any).gtag('config', 'G-RFLQ46KEG1', {
          page_path: pathname,
          page_location: window.location.href,
          page_title: document.title
        });
      }

      // Meta Pixel: ViewContent bei Seitenaufruf (DSGVO-konform)
      try {
        const c = localStorage.getItem('cookieConsent');
        const consent = c ? JSON.parse(c) : null;
        const marketingAllowed = !!(consent && consent.marketing);
        const fbq = (window as any).fbq as undefined | ((...args: any[]) => void);
        if (marketingAllowed && typeof fbq === 'function') {
          fbq('track', 'ViewContent');
          (window as any).__fbq_last_viewcontent = { path: pathname, ts: Date.now() };
        }
      } catch {}
    }, 100);
    
    // Also track with custom analytics
    import('./utils/analytics').then(({ trackPageView }) => {
      trackPageView(pathname);
    });
  }, [pathname]);

  return null;
}

// Resolve a safe Router basename from PUBLIC_URL
const resolveBasename = (raw?: string): string => {
  const value = (raw || '').trim();
  if (!value || value === '.' || value === './') return '';
  // Strip protocol + host if a full URL was provided
  const stripped = value.replace(/^https?:\/\/[^/]+/i, '');
  if (!stripped) return '';
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
};

function App() {
  const [, setCookiePreferences] = useState(null);
  const basename = resolveBasename(process.env.PUBLIC_URL);

  // Initialize analytics when component mounts - lazy loaded
  useEffect(() => {
    // Initialize analytics with a delay and lazy loading
    const timer = setTimeout(() => {
      import('./utils/analytics').then(({ initializeAnalytics }) => {
        initializeAnalytics();
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleCookieConsent = (preferences: any) => {
    setCookiePreferences(preferences);
    // Here you would typically initialize or disable third-party services
    // based on the user's preferences
    if (preferences.analytics) {
      // Initialize Google Analytics and Umami
      console.log('Analytics cookies accepted');
      // Load Google Analytics via the global function
      if (window.loadGoogleAnalytics) {
        window.loadGoogleAnalytics();
      }
      // Reinitialize Umami if it wasn't loaded before
      import('./utils/analytics').then(({ initializeAnalytics }) => {
        initializeAnalytics();
      });
    }
    if (preferences.marketing) {
      // Initialize marketing tools
      console.log('Marketing cookies accepted');
    }
    if (preferences.functional) {
      // Initialize functional cookies
      console.log('Functional cookies accepted');
    }
  };

  return (
    <HelmetProvider>
      <Router basename={basename || undefined}>
        <ScrollToTop />
        <Layout>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/uber-uns" element={<About />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Privacy />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletter-anmeldung" element={<NewsletterSubscription />} />
            <Route path="/newsletter-bestaetigung" element={<NewsletterConfirmation />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/formular" element={<LeadFormular />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/einzugsgebiete" element={<Einzugsgebiete />} />
            <Route path="/checkliste" element={<ChecklisteDownload />} />
            <Route path="/kapitalanlage" element={<Kapitalanlage />} />
            <Route path="/kapitalanlage-kaufen" element={<KapitalanlageKaufen />} />
            <Route path="/kapitalanlagen-guide" element={<KapitalanlagenGuide />} />
            <Route path="/ueber-seidels-immobilien" element={<UeberSeidelsImmobilien />} />
          </Routes>
        </Suspense>
      </Layout>
        <Suspense fallback={null}>
          <CookieConsent onAccept={handleCookieConsent} />
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

export default App;
